'use client'

/**
 * The React side of the icon sets (spec docs/superpowers/specs/2026-10-29-branding-kit-and-brand-theme-design.md,
 * section 3.6a): read the store, keep it in step with the account, and save a person's own click. The app theme's
 * shape (app-themes/use-app-theme.ts), for a smaller store (./icon-sets.ts).
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { fetchApi } from '@/lib/api/client'
import { useShellTranslation } from '@/lib/shell/use-shell-translation'
import { isViewingAsAnotherUser } from '@/lib/auth/viewing-as-another-user'
import { themeRefusal, themeRefusalKey, useThemeWorkspaceId } from './app-themes/use-app-theme'
import {
  adoptAccountIconSet,
  beginIconSetSave,
  endIconSetSave,
  getIconSetSnapshot,
  getSavedIconSet,
  getServerIconSetSnapshot,
  iconSetSaveEpoch,
  isIconSet,
  setIconSet,
  subscribeIconSet,
  type IconSet,
  type IconSetSnapshot,
} from './icon-sets'

export const ICON_SET_PATH = '/users/me/preferences/icon-set'

/** One cache entry per signed-in user. */
export const iconSetQueryKey = (userId: string | null) => ['users', 'me', 'icon-set', userId] as const

export function useIconSetState(): IconSetSnapshot {
  return useSyncExternalStore(subscribeIconSet, getIconSetSnapshot, getServerIconSetSnapshot)
}

interface AccountAnswer {
  iconSet: IconSet | null
  /** The save epoch when the question was asked (./icon-sets.ts). */
  askedAt: number
}

/** A set; null for none (never chosen) and anything outside the catalogue; undefined when it is no answer at all. */
function iconSetFromAnswer(answer: unknown): IconSet | null | undefined {
  if (typeof answer !== 'object' || answer === null || !('iconSet' in answer)) return undefined
  const { iconSet } = answer as { iconSet: unknown }
  return isIconSet(iconSet) ? iconSet : null
}

/**
 * Ask the account for the signed-in user's set, once per user, and adopt the answer unless a save overtook it. With a
 * workspace, the brand read is on its way too, and the store only records the answer until it lands (./icon-sets.ts). While
 * a super-admin views as someone, nothing stays in this browser: a cookie would carry the viewed person's id.
 */
export function useIconSetAccountSync(userId: string | null): void {
  const readComing = useThemeWorkspaceId() !== null
  const { data } = useQuery({
    queryKey: iconSetQueryKey(userId),
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<AccountAnswer | null> => {
      const askedAt = iconSetSaveEpoch()
      const iconSet = iconSetFromAnswer(await fetchApi<unknown>(ICON_SET_PATH))
      return iconSet === undefined ? null : { iconSet, askedAt }
    },
  })

  useEffect(() => {
    if (data && userId) adoptAccountIconSet(data.iconSet, isViewingAsAnotherUser() ? null : userId, data.askedAt, readComing)
  }, [data, userId, readComing])
}

// A run of saves shares one rollback point, as the app theme's do: the last set the account confirmed. Module-level,
// because the menu that saves unmounts when its popover closes.
let latestSave = 0
let savesRunning = 0
let confirmed: { iconSet: IconSet | null } | null = null

/**
 * A person's own click: the page and the cookie change at once and go back, with a toast, if the save fails. While a
 * super-admin views the app as a user, the set applies to the page only.
 */
export function useSaveIconSet(userId: string | null): (next: IconSet) => Promise<boolean> {
  const queryClient = useQueryClient()
  const { t } = useShellTranslation()
  return useCallback(
    async (next: IconSet): Promise<boolean> => {
      if (!isIconSet(next)) return false
      const current = getSavedIconSet()
      if (isViewingAsAnotherUser()) {
        setIconSet(next, null)
        return true
      }
      const mine = ++latestSave
      if (savesRunning === 0) confirmed = { iconSet: current }
      savesRunning += 1
      beginIconSetSave()
      setIconSet(next, userId)
      try {
        const answer = iconSetFromAnswer(await fetchApi<unknown>(ICON_SET_PATH, { method: 'PUT', body: JSON.stringify({ iconSet: next }) }))
        const saved = answer ?? next
        if (mine === latestSave) {
          confirmed = { iconSet: saved }
          if (saved !== next) setIconSet(saved, userId)
          queryClient.setQueryData<AccountAnswer>(iconSetQueryKey(userId), { iconSet: saved, askedAt: -1 })
        }
        return true
      } catch (error) {
        if (mine === latestSave) {
          const back = confirmed ? confirmed.iconSet : current
          setIconSet(back, userId)
          const because = themeRefusalKey(themeRefusal(error))
          toast.error(t('shell.theme.app.iconsNotSaved'), {
            description: t(because ?? 'shell.theme.app.iconsBackTo', { set: t(`shell.theme.app.iconSet.${getIconSetSnapshot().set}.name`) }),
          })
        }
        return false
      } finally {
        savesRunning -= 1
        endIconSetSave()
      }
    },
    [queryClient, userId, t],
  )
}

/** Tests only. */
export function resetIconSetSavesForTests(): void {
  latestSave = 0
  savesRunning = 0
  confirmed = null
}
